import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import AuthPage from '@/pages/AuthPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import DashboardLayout from '@/components/DashboardLayout';
import Dashboard from '@/pages/Dashboard';
import UploadData from '@/pages/UploadData';
import SpendAnalysis from '@/pages/SpendAnalysis';
import SpendDistribution from '@/pages/SpendDistribution';
import CategoryConsole from '@/pages/CategoryConsole';
import CommonalityAnalysis from '@/pages/CommonalityAnalysis';
import Suppliers from '@/pages/Suppliers';
import SupplierDirectory from '@/pages/SupplierDirectory';
import ComparativeAnalysis from '@/pages/ComparativeAnalysis';
import Reports from '@/pages/Reports';
import UserManagement from '@/pages/UserManagement';
import Organizations from '@/pages/Organizations';
import P2PAnalytics from '@/pages/P2PAnalytics';
import DataQuality from '@/pages/DataQuality';
import Settings from '@/pages/Settings';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import AuditLog from '@/pages/AuditLog';
import IndustryBenchmark from '@/pages/IndustryBenchmark';
import Connectors from '@/pages/Connectors';
import SpendForecasting from '@/pages/SpendForecasting';
import CurrencyAnalytics from '@/pages/CurrencyAnalytics';
import GovernanceDashboard from '@/pages/GovernanceDashboard';
import InvoiceAging from '@/pages/InvoiceAging';
import OnContractMaverick from '@/pages/OnContractMaverick';
import InvoiceDedup from '@/pages/InvoiceDedup';
import ReviewQueue from '@/pages/ReviewQueue';
import HelpPage from '@/pages/HelpPage';
import AbuseEvents from '@/pages/AbuseEvents';
import PlatformAdmin from '@/pages/PlatformAdmin';
import CookieConsent from '@/components/CookieConsent';
import { ThemeProvider } from '@/components/ThemeProvider';
import '@/App.css';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // SECURITY: Proactively sweep any legacy/unscoped client caches from previous
    // app versions so no filter/insight can bleed across users on shared browsers.
    // New keys use the pattern `pca_filters_<orgId>_<page>` / `ai_cache_<orgId>_<page>_...`.
    // Any key that matches the old `pca_filters_<page>` or `ai_cache_<page>` pattern
    // (no org prefix) is untrusted and removed.
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const orgId = user?.organization_id || null;
      const keys = Object.keys(localStorage);
      for (const k of keys) {
        if (k.startsWith('pca_filters_') || k.startsWith('ai_cache_')) {
          // Keep only keys that are org-scoped to the CURRENT user's org.
          const expectedPrefix = orgId
            ? (k.startsWith('pca_filters_') ? `pca_filters_${orgId}_` : `ai_cache_${orgId}_`)
            : null;
          if (!expectedPrefix || !k.startsWith(expectedPrefix)) {
            localStorage.removeItem(k);
          }
        }
      }
    } catch {}

    // NOTE: Auth token is no longer read from localStorage — it lives in an
    // httpOnly cookie the browser sends automatically. We still gate the initial
    // render on the cached user object so the shell doesn't flash the login page
    // while /auth/me is round-tripping.
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
    setLoading(false);
  }, []);

  const handleLogin = (userData, _token) => {
    // SECURITY: clear any cached per-page filters / AI insights from a previous user
    // to prevent cross-tenant UI cache leakage on shared browsers.
    try {
      const keys = Object.keys(localStorage);
      for (const k of keys) {
        if (k.startsWith('pca_filters_') || k.startsWith('ai_cache_')) {
          localStorage.removeItem(k);
        }
      }
    } catch {}
    // The JWT itself is set by the server as an httpOnly cookie — we never persist
    // it in JS-accessible storage. Only the (non-credential) user profile is cached.
    localStorage.removeItem('token');
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = async () => {
    // SECURITY: clear all org-scoped client caches on logout.
    try {
      const keys = Object.keys(localStorage);
      for (const k of keys) {
        if (k.startsWith('pca_filters_') || k.startsWith('ai_cache_')) {
          localStorage.removeItem(k);
        }
      }
    } catch {}
    // Ask the server to clear the httpOnly cookie + revoke the session.
    try {
      const api = (await import('./lib/api')).default;
      await api.post('/auth/logout');
    } catch {}
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <ThemeProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route
              path="/auth"
              element={!user ? <AuthPage onLogin={handleLogin} /> : <Navigate to="/" />}
            />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route
              path="/"
              element={user ? <DashboardLayout user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />}
            >
              <Route index element={<Dashboard />} />
              <Route path="upload" element={<UploadData />} />
              <Route path="spend-analysis" element={<SpendAnalysis />} />
              <Route path="spend-distribution" element={<SpendDistribution />} />
              <Route path="category-console" element={<CategoryConsole />} />
              <Route path="commonality-analysis" element={<CommonalityAnalysis />} />
              <Route path="suppliers" element={<Suppliers />} />
              <Route path="user-management" element={<UserManagement />} />
              <Route path="organizations" element={<Organizations />} />
              <Route path="supplier-directory" element={<SupplierDirectory />} />
              <Route path="comparative-analysis" element={<ComparativeAnalysis />} />
              <Route path="p2p-analytics" element={<P2PAnalytics />} />
              <Route path="data-quality" element={<DataQuality />} />
              <Route path="reports" element={<Reports />} />
              <Route path="settings" element={<Settings />} />
              <Route path="privacy-policy" element={<PrivacyPolicy />} />
              <Route path="audit-log" element={<AuditLog />} />
              <Route path="industry-benchmark" element={<IndustryBenchmark />} />
              <Route path="connectors" element={<Connectors />} />
              <Route path="forecasting" element={<SpendForecasting />} />
              <Route path="currency-analytics" element={<CurrencyAnalytics />} />
              <Route path="governance" element={<GovernanceDashboard />} />
              <Route path="invoice-aging" element={<InvoiceAging />} />
              <Route path="on-contract-maverick" element={<OnContractMaverick />} />
              <Route path="invoice-dedup" element={<InvoiceDedup />} />
              <Route path="review-queue" element={<ReviewQueue />} />
              <Route path="help" element={<HelpPage />} />
              <Route path="signals" element={<Navigate to="/?tab=signals" replace />} />
              <Route path="admin/abuse-events" element={<AbuseEvents />} />
              <Route path="admin/platform" element={<PlatformAdmin />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
        <CookieConsent />
      </div>
    </ThemeProvider>
  );
}

export default App;
